import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_tmb-foundation/artifacts/v9zjv1mj_TMB%20Logo.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/vision", label: "Our Vision" },
  { to: "/mission", label: "Our Mission" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-white/50 backdrop-blur-md"
      }`}
    >
      <div className="container-tmb flex items-center justify-between py-3 px-4 md:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="TMB Global Foundation"
            className="w-12 h-12 md:w-14 md:h-14 object-contain group-hover:scale-105 transition-transform"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-lg md:text-xl text-[#1A2421]">
              TMB Global
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#2C4C3B] font-bold">
              Foundation
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/donate" data-testid="nav-donate-cta" className="btn-primary">
            <Heart className="w-4 h-4" />
            Donate
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#1A2421]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-black/5 px-4 pb-6 pt-2"
        >
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 px-2 border-b border-black/5 ${
                    isActive ? "text-[#C85A3A] font-semibold" : "text-[#1A2421]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/donate"
              data-testid="mobile-donate-cta"
              className="btn-primary mt-4 justify-center"
            >
              <Heart className="w-4 h-4" /> Donate Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
